import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import SavingWheel from '../../../Parts/SavingWheel';
import TextDivider from '../../../Parts/TextDivider';
import PaymentStep from '../PaymentStep';
import NewCardField, { CardAccount } from '../../../Parts/NewCardField';
import { UseMultipliersByQuoteId, useQuotePricing } from '../../../Store/entities/quotes/hooks';
import apiCalls from '../../../PowerShadesAPIFunctions';
import { useQuoteDemoReview } from '../../../Store/entities/quoteDemoReviews/hooks';
import { QuoteStore } from '../../../Store/entities/entitiesType';

type CardPaymentProps = {
	quote: QuoteStore;
	quoteId: number;
	quoteType: string;
	halfPrice: string;
	setSubmit: (submitFunction: (card: any) => Promise<string>) => void;
	showSubmit: () => void;
	setProcessing: (processing: boolean) => void;
	done?: () => void;
};

const CardPayment = ({
	quote,
	quoteId,
	quoteType,
	halfPrice,
	setSubmit,
	showSubmit,
	setProcessing,
	done
}: CardPaymentProps) => {
	const [waiting, setWaiting] = useState(false);
	const [error, setError] = useState<null | string>(null);
	const [, setSaveCard] = useState(false);

	const [isActuallyAccount, setIsActuallyAccount] = useState(false);

	const multipliers = UseMultipliersByQuoteId(quoteId);
	console.log('multipliers', multipliers);

	const price = useQuotePricing(quoteId);

	const payAmount = multipliers?.list.find((m) => m.label.includes("My Price"))
	?? multipliers?.list.find((m) => m.label.includes("Dealer Price"))
	?? multipliers?.list[0];
	
	const payPricing = useQuotePricing(quoteId, payAmount?.multiplier);
	const msrpPricing = useQuotePricing(quoteId, 1);

	const demo = useQuoteDemoReview(quoteId);

	const isDemo = demo?.demo_review_status === 'approved';
	const demoPercentage = Math.min(Math.max(demo?.value ?? 0, 0), 100);
	const remainingPercent = 1 - (demoPercentage / 100);

	const percentDemoPrice = (
		(msrpPricing?.total_price ?? 0)
		* remainingPercent)
		+ (msrpPricing?.total_taxes ?? 0)
		+ (msrpPricing?.total_shipping_price ?? 0);
	const demoPrice = demo?.name === "percentage" ? percentDemoPrice : demo?.value ?? 0;

	const couponAmount = msrpPricing?.coupon_total ?? 0;

	const expectedCardTotal = isDemo ? demoPrice : ((payPricing?.subtotal ?? 0) - (payPricing?.coupon_total  ?? 0 ));

	if (!price || !quote || (isDemo && !demo)) {
		return (
			<PaymentStep>
				<TextDivider>
					<h3 className={`text-${quoteType}`} style={{ fontWeight: 'normal' }}>
						Card/ACH Payment
					</h3>
				</TextDivider>
				<div className="error">Loading...</div>
			</PaymentStep>
		);
	}

	const submit = (card: { id: string;
		is_account: boolean;
	}) => {
		setProcessing(true);

		setWaiting(true);

		return new Promise<string>((resolve, reject) => {
			apiCalls.submitCreditCardPayment(
				quoteId,
				card.id,
				card.is_account,
				expectedCardTotal
			).then((resp) => {
				if (resp.status !== 200 || resp.data.success === false) {
					reject(Error('There was an error processing your payment.'));
					setWaiting(false);
					setError('There was an error processing your payment.');
					console.error(JSON.stringify(resp));
					Sentry.captureException(Error(resp?.data?.error
							?? 'Something went wrong processing a payment - check console for more details'));
					showSubmit();
					resolve('Payment unsuccessful');
				} else {
					resolve('Payment successful');
					done && done();
				}
			});
		});
	};
	const cardChosen = (card: CardAccount) => {
		setSaveCard(true);
		showSubmit();
		setSubmit(() => submit({
			id: card.id,
			is_account: card.isAccount
		}));
	};

	return (
		<PaymentStep>
			<TextDivider>
				<h3 className={`text-${quoteType}`} style={{ fontWeight: 'normal' }}>
					Card/ACH Payment
				</h3>
			</TextDivider>

			<>
				<NewCardField
					quoteType={quoteType}
					isActuallyAccount={isActuallyAccount}
					setIsActuallyAccount={setIsActuallyAccount}
					half={halfPrice}
					quoteId={quote.id}
					setPaymentType={(cardType) => {
						cardChosen(cardType);
					}}
				/>

				<br />
			</>

			{error ? (
				<div className="error">
					<br />
					{error}
				</div>
			) : null}

			<div style={{ textAlign: 'right', width: '100%' }}>
				{waiting ? <SavingWheel status={1} customText="Please Wait.." /> : null}
			</div>
		</PaymentStep>
	);
};

export default CardPayment;
